<template>
  <loader :is-loading="loading" />
  <div v-on-click-outside="() => (showMenu = false)" class="relative">
    <primary-button
      class="z-10 h-10"
      :disabled="runningOfDataOrchestrationDisabled"
      @click="showMenu = !showMenu"
    >
      <span class="mr-6">
        {{ buttonText }}
      </span>
    </primary-button>

    <div
      data-cy="templates-dropdown"
      class="absolute top-0 right-0 flex h-10 w-10 transform cursor-pointer items-center justify-center rounded-r transition-all"
      :class="{
        'bg-blue-800': showMenu,
        'pointer-events-none cursor-not-allowed':
          runningOfDataOrchestrationDisabled,
      }"
      @click="showMenu = !showMenu"
    >
      <icon-base
        class="translate-x-0.5 translate-y-1 transform text-white"
        :icon="IconArrowUp"
      />
    </div>

    <!-- If DO hasn't been run on this deal yet, just show the list of DO templates to choose from -->
    <data-orchestration-templates-menu
      v-if="!lastHistoryLog && showMenu"
      :show="showMenu"
      @select="execute"
    />

    <!-- Otherwise, provide additional options to resume or re-run the previous template -->
    <ul
      v-else
      class="absolute right-0 rounded-sm bg-white shadow-lg transition-all"
      :class="{
        'top-9 -z-1 opacity-0': !showMenu,
        'top-11 z-40': showMenu,
        'deleted-template': isDataOrchestrationTemplateDeleted,
      }"
    >
      <!-- Deleted template -->
      <li
        v-if="isDataOrchestrationTemplateDeleted"
        class="rounded-t bg-red-50 p-2 text-xs font-semibold text-red-400 capitalize"
      >
        {{ $t("ORCHESTRATION.TEMPLATE_DELETED") }}
      </li>

      <!-- Resume Options -->
      <li
        v-for="menuItem in resumeMenuItems"
        :key="menuItem.resumeOption"
        class="menu-item rerun-menu-item"
        @click="resume(menuItem.resumeOption)"
      >
        <icon-base :icon="menuItem.icon" class="menu-item-icon" />
        {{ menuItem.label }}
      </li>

      <!-- Run new DO: Allow the user to run DO again with a different template -->
      <li
        class="menu-item templates-menu-item"
        :class="{ active: showTemplatesSubmenu }"
        @mouseenter="showTemplatesSubmenu = true"
        @mouseleave="showTemplatesSubmenu = false"
      >
        <icon-base :icon="IconOrchestration" class="menu-item-icon" />
        {{ $t("ORCHESTRATION.RUN_NEW") }}
        <icon-base :icon="IconArrowRight" class="ml-4" />

        <data-orchestration-templates-menu
          v-if="showTemplatesSubmenu"
          :class="{ 'h-0': !showTemplatesSubmenu }"
          :show="showTemplatesSubmenu"
          @select="execute"
        />
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import { storeToRefs } from "pinia";

import { DATA_ORCHESTRATION_MASTER_STATUSES } from "@/helpers/constants";
import {
  UNDERWRITING_STATUS_FAIL,
  UNDERWRITING_STATUS_PASS,
  UNDERWRITING_STATUS_PAUSED,
} from "@/helpers/constants/underwriting";
import { useAuth } from "@/hooks/auth";
import { useClients } from "@/hooks/clients";
import { useDeals } from "@/hooks/deals";
import { useUnderwritingLogs } from "@/hooks/underwriting";
import { useActiveStage } from "@/hooks/workflow";
import useApplicationsStore from "@/stores/applications";

import DataOrchestrationTemplatesMenu from "./DataOrchestrationTemplatesMenu.vue";
import IconArrowRight from "@/components/icons/IconArrowRight.vue";
import IconArrowUp from "@/components/icons/IconArrowUp.vue";
import IconOrchestration from "@/components/icons/IconOrchestration.vue";
import PrimaryButton from "@/components/ui/buttons/PrimaryButton.vue";

import type {
  DataOrchestrationOutcome,
  IOrchestrationTemplate,
} from "@/models/orchestration";

enum ResumeOption {
  PASS_RESUME,
  FAIL_RESUME,
  RETRY_STEP,
  RERUN_ORCHESTRATION,
}

type ResumeMenuItem = {
  isVisible: boolean;
  resumeOption: ResumeOption;
  icon: string;
  label: string;
};

const { dispatch } = useStore();
const {
  activeDeal,
  canEditDealApplicationAndUnderwriting,
  getParamsForRetrievingDOTemplates,
} = useDeals();
const { masterStatus, lastHistoryLog, outcome } = useUnderwritingLogs();
const { activeStage, isUnderwritingStage } = useActiveStage();
const { t } = useI18n();
const { isClient } = useAuth();
const { authClientSettings } = useClients();

const applicationsStore = useApplicationsStore();
const { activeDataOrchestrationLog } = storeToRefs(applicationsStore);
const loading = ref(false);
const showMenu = ref(false);
const showTemplatesSubmenu = ref(false);

const satisfiesClientPermissions = computed(() => {
  return (
    !isClient ||
    (isClient && !!authClientSettings.value?.can_manage_data_orchestration)
  );
});

const isCompletePass = computed(
  () => masterStatus.value === DATA_ORCHESTRATION_MASTER_STATUSES.COMPLETE_PASS
);

const isProcessing = computed(
  () => masterStatus.value === DATA_ORCHESTRATION_MASTER_STATUSES.PROCESSING
);

const buttonText = computed(() => {
  if (!lastHistoryLog.value && !isProcessing.value) {
    return t("ORCHESTRATION.RUN_ORCH");
  }
  return t("ORCHESTRATION.ORCHESTRATION");
});

const runningOfDataOrchestrationDisabled = computed(
  () =>
    !canEditDealApplicationAndUnderwriting.value ||
    isProcessing.value ||
    !satisfiesClientPermissions.value
);

const isDataOrchestrationTemplateDeleted = computed(
  () =>
    activeDataOrchestrationLog.value?.template !== null &&
    activeDataOrchestrationLog.value?.template !== undefined &&
    activeDataOrchestrationLog.value?.template?.deleted_at
);

const resumeMenuItems = computed<ResumeMenuItem[]>(() =>
  [
    // Pass-resume
    {
      isVisible:
        ![UNDERWRITING_STATUS_PASS, UNDERWRITING_STATUS_PAUSED].includes(
          outcome.value
        ) &&
        hasAppropriateNextStep("pass") &&
        !isCompletePass.value,
      resumeOption: ResumeOption.PASS_RESUME,
      icon: "check-circle",
      label: t("ORCHESTRATION.PASS_RESUME"),
    },
    // Fail-resume
    {
      isVisible:
        ![UNDERWRITING_STATUS_FAIL, UNDERWRITING_STATUS_PAUSED].includes(
          outcome.value
        ) && hasAppropriateNextStep("fail"),
      resumeOption: ResumeOption.FAIL_RESUME,
      icon: "x-circle",
      label: t("ORCHESTRATION.FAIL_RESUME"),
    },
    // Re-run last condition
    {
      isVisible: true,
      resumeOption: ResumeOption.RETRY_STEP,
      icon: "reset",
      label: t("ORCHESTRATION.RE_RUN_CONDITION"),
    },
    // Restart DO using previous template
    {
      isVisible: true,
      resumeOption: ResumeOption.RERUN_ORCHESTRATION,
      icon: "restart",
      label: t("ORCHESTRATION.RESTART_ORCHESTRATION"),
    },
  ].filter((option) => option.isVisible)
);

const RESUME_ENDPOINTS: Record<ResumeOption, string> = {
  [ResumeOption.PASS_RESUME]: "orchestration/continuePass",
  [ResumeOption.FAIL_RESUME]: "orchestration/continueFail",
  [ResumeOption.RETRY_STEP]: "orchestration/retryStep",
  [ResumeOption.RERUN_ORCHESTRATION]:
    "applications/executeDataOrchestrationTemplate",
};

const resume = async (resumeOption: ResumeOption) => {
  if (isDataOrchestrationTemplateDeleted.value) {
    return;
  }

  showMenu.value = false;
  if (loading.value) {
    return;
  }
  loading.value = true;
  const payload =
    resumeOption === ResumeOption.RERUN_ORCHESTRATION
      ? {
          id: activeDeal.value.id,
          template_id: activeDataOrchestrationLog.value?.template?.id,
        }
      : activeDataOrchestrationLog.value?.id;
  try {
    await dispatch(RESUME_ENDPOINTS[resumeOption], payload);
  } finally {
    loading.value = false;
  }
};

const execute = async (template: IOrchestrationTemplate) => {
  showMenu.value = false;
  if (loading.value) {
    return;
  }
  loading.value = true;
  try {
    await dispatch("applications/executeDataOrchestrationTemplate", {
      id: activeDeal.value.id,
      template_id: template.id,
    });
  } finally {
    loading.value = false;
  }
};

const hasAppropriateNextStep = (outcomeToFind: DataOrchestrationOutcome) => {
  const currentStep =
    activeDataOrchestrationLog.value?.template?.content?.steps.find(
      (step) => step.id === lastHistoryLog.value?.step_id
    );
  const rightOutcome = currentStep?.outcomes.find(
    (outcome) => outcome.outcome === outcomeToFind
  );
  return !!rightOutcome?.next_step;
};

watch(
  activeStage,
  () => {
    if (satisfiesClientPermissions.value && isUnderwritingStage.value) {
      const payload = getParamsForRetrievingDOTemplates(activeDeal.value, {
        forWorkflow: true,
      });
      dispatch("orchestration/getAllTemplates", payload);
    }
  },
  { immediate: true }
);
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.menu-item {
  @apply relative cursor-pointer px-2 py-2 whitespace-nowrap text-gray-700 hover:bg-gray-100;
}

.menu-item-icon {
  @apply mr-2 opacity-30;
}

.deleted-template .rerun-menu-item {
  @apply pointer-events-none cursor-default text-disabled-medium;
}

.templates-menu-item.active {
  @apply bg-gray-100;
}

/* This makes sure the child menu doesn't close when moving the mouse from the parent menu item into child */
.templates-menu-item.active::after {
  content: "";
  display: block;
  position: absolute;
  left: 0;
  top: 100%;
  height: 2rem;
  width: 100%;
}
</style>
